/* eslint-disable react/no-unstable-nested-components */
import CVMSquaredIcon from "components/icons/CVMSquaredIcon";
import MorningstarSquaredIcon from "components/icons/MorningstarSquaredIcon";
import { PrimitiveSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import { invariant } from "outvariant";
import { IIndicatorDataSource } from "types/graphqlTypes";

const AVAILABLE_DATA_SOURCES = [
  {
    label: "CVM",
    dataSource: IIndicatorDataSource.Cvm,
    icon: <CVMSquaredIcon className="inline-flex squircle" />,
    triggerClassName: "border-green-800/20 bg-green-600/10 hover:ring-green-500/10 hover:ring-offset-green-900/30",
    labelClassName: "text-green-700 dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "hover:bg-green-300/10 data-[state=checked]:bg-green-600/10 data-[state=checked]:text-green-700",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
  {
    label: "Morningstar",
    dataSource: IIndicatorDataSource.Morningstar,
    icon: <MorningstarSquaredIcon className="inline-flex squircle" />,
    triggerClassName: "border-red-800/20 bg-red-300/10 hover:ring-red-500/10 hover:ring-offset-red-900/30",
    labelClassName: "text-[#f42706] dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "hover:bg-red-300/10 data-[state=checked]:bg-red-600/5 data-[state=checked]:text-[#f42706]",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
];

interface SelectIndicatorsDataSourceProps {
  value: IIndicatorDataSource;
  onChange: (value: IIndicatorDataSource) => void;
  disabled?: boolean;
}
export default function SelectIndicatorsDataSource({
  value,
  disabled = false,
  onChange,
}: SelectIndicatorsDataSourceProps) {
  const theme = useTheme();
  const selectedOption = AVAILABLE_DATA_SOURCES.find(({ dataSource }) => value === dataSource);
  invariant(selectedOption, `Invalid selected data source: "%s"`, value);

  return (
    <Select value={value} disabled={disabled} onValueChange={(value) => onChange(value as IIndicatorDataSource)}>
      <SelectTrigger
        hideChevronIcon={true}
        className={classNames(
          "inline-flex",
          "w-full min-w-36 border border-slate-200 bg-white lg:max-w-36",
          "font-aeonik text-sm font-medium tracking-wide text-slate-600",
          "hover:ring-2 focus-visible:ring-2",
          "hover:ring-offset-1 focus-visible:ring-offset-1",
          "hover:border-transparent hover:ring-slate-100 hover:ring-offset-slate-300",
          "focus-visible:border-transparent focus-visible:ring-indigo-100 focus-visible:ring-offset-indigo-300",
          "disabled:select-none",
          "truncate",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
          "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
          "rounded-xl",
          selectedOption.triggerClassName,
        )}
      >
        <SelectValue asChild={true}>
          <div className="pointer-events-none flex w-full items-center justify-between space-x-2">
            <strong className={classNames("text-xs font-bold tracking-wide", selectedOption.labelClassName)}>
              {selectedOption.label}
            </strong>
            {selectedOption.icon}
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent
        side="top"
        scrollAreaViewportClassName="space-y-1"
        className={classNames("min-w-36 rounded-2xl border border-slate-300 text-sm", {
          "border-slate-900 bg-slate-700 text-slate-300 shadow-neutral-950/40": theme === "dark",
          dark: theme === "dark",
        })}
      >
        {AVAILABLE_DATA_SOURCES.map(({ label, icon, dataSource, itemClassName }) => {
          return (
            <PrimitiveSelectItem key={label} value={dataSource} className={itemClassName}>
              <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                <h4 className="inline-flex text-xs font-bold dark:text-slate-300">{label}</h4>
                {icon}
              </div>
            </PrimitiveSelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
